import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import { Icon } from "@iconify/react";
import { useTheme } from "@mui/material/styles";
import CancelIcon from "@mui/icons-material/Cancel";
import Stack from "@mui/material/Stack";
import Grid from "@mui/material/Grid";
import ErrorIcon from "@mui/icons-material/Error";
import LinearProgress from "@mui/material/LinearProgress";
import { ButtonLink } from "./Image";

import { Image, ImageProps } from "./Image";
import { Pdf } from "./Pdf";
import { TextPreview } from "./TextPreview";
import { CsvPreview } from "./CsvPreview";

import React, { useEffect, useState, useRef } from "react";
import { MAX_FILE_SIZE_PREVIEW } from "./constants";
import { AttachedFile, MdPiece, MessageItem } from "./MessageItem";
import { EmbeddedFile } from "./EmbeddedFile";

import { useDownloadAndOpenFile } from "./hooks";
import { imageToggleAtom } from "./JotaiAtoms";
import { useAtomValue } from "jotai";
import CropOriginalIcon from "@mui/icons-material/CropOriginal";
import JSONPretty from "react-json-pretty";
import * as riki from "jsriki";
import { toBytes } from "fast-base64";
import { parseMarkdown } from "./Markdown";

export type PreviewFileInfo = {
  url: string;
  name: string;
  filetype: string;
  mimetype: string;
  index: number;
  size: number;
};

export function isTextMimeType(mimetype?: string) {
  return (
    mimetype &&
    (mimetype.startsWith("text/") ||
      mimetype === "application/json" ||
      mimetype === "application/x-csh" ||
      mimetype === "application/x-sh" ||
      mimetype === "application/xml" ||
      mimetype === "application/javascript" ||
      mimetype === "application/x-javascript")
  );
}

const isImage = (f?: { type?: string }) => {
  const out = f?.type ? f.type.startsWith("image/") : false;
  return out;
};

const isPdf = (f?: { type?: string }) => {
  const out = f ? f.type === "application/pdf" : false;
  return out;
};

const isJson = (f?: { type?: string }) => {
  const out = f ? f.type === "application/json" : false;
  return out;
};

const isCsv = (f?: { type?: string; name?: string }) => {
  if (!f) return false;
  if (f.type === "text/csv" || f.type === "application/csv") return true;
  // Also check file extension
  if (f.name && f.name.toLowerCase().endsWith(".csv")) return true;
  return false;
};

type IndexedFile = {
  fileOrUrl: File | string;
  index: number;
  name?: string;
  type?: string;
  size?: number;
  preview?:
    | "image"
    | "text"
    | "pdf"
    | "json"
    | "diff"
    | "csv"
    | "audio"
    | "video";
};

const getFileKey = (f: IndexedFile) => {
  if (f.fileOrUrl instanceof File) {
    return `${f.name}-${f.size}`;
  } else {
    return `${f.name || f.fileOrUrl || ""}-${f.type || ""}-${f.size || ""}`;
  }
};

export function categorizeFilesForDisplay(
  files: (File | AttachedFile)[],
  startIndex?: number,
  usePreviews?: boolean
) {
  let newPreviews: IndexedFile[] = [];
  let newLinks: IndexedFile[] = [];

  const off: number = startIndex === undefined ? 0 : startIndex;

  files.forEach((f, i) => {
    const index = i + off;

    let fileAttrs = {
      name: f.name,
      size: f.size,
      type: f.type,
      fileOrUrl:
        f instanceof File
          ? f
          : f.uri
          ? f.uri
          : new File([f.content!], f.name || "attachment", { type: f.type }),
    };

    if (usePreviews && !(f instanceof File) && f.image_preview) {
      fileAttrs.fileOrUrl = f.image_preview;
      fileAttrs.type = "image/jpeg";
      fileAttrs.size = undefined;
    }

    if (f.size && f.size > MAX_FILE_SIZE_PREVIEW && !(f instanceof File)) {
      newLinks.push({ ...fileAttrs, index });
    } else if (isPdf(f)) {
      newPreviews.push({ ...fileAttrs, index, preview: "pdf" });
    } else if (isImage(f)) {
      // f = {
      //   uri: "s3://topstorie-user-uploads-prod2/cognito/topstorie/fc536aec-a5fd-4b71-8612-473d9c2fd30d/test-74723716-a483-478e-8d8c-04437b278fdb-123",
      //   type: "image/unknown",
      // };
      newPreviews.push({ ...fileAttrs, index, preview: "image" });
    } else if (isJson(f)) {
      newPreviews.push({ ...fileAttrs, index, preview: "json" });
    } else if (isCsv(f)) {
      newPreviews.push({ ...fileAttrs, index, preview: "csv" });
    } else if (f.type === "text/x-diff") {
      newPreviews.push({ ...fileAttrs, index, preview: "diff" });
    } else if (isTextFileType(f)) {
      newPreviews.push({ ...fileAttrs, index, preview: "text" });
    } else if (f.type && f.type.startsWith("audio/")) {
      newPreviews.push({ ...fileAttrs, index, preview: "audio" });
    } else if (f.type && (f.type === "video/mp4" || f.type === "video/webm")) {
      newPreviews.push({ ...fileAttrs, index, preview: "video" });
    } else {
      newLinks.push({ ...fileAttrs, index });
    }
  });

  if (newPreviews.length + newLinks.length !== files.length) {
    throw new Error();
  }

  return { previews: newPreviews, links: newLinks };
}

const collectEmbeddedImagesFromMarkdown = (pp: MdPiece[], urls: string[]) => {
  for (const { t, v } of pp) {
    if (v) {
      if (t === "img" || t === "fx") {
        const uri = (v as string).split("|")[0];
        urls.push(uri);
      } else if (t == "t") {
        const table = v as MdPiece[][][];
        for (const row of table) {
          for (const cell of row) {
            collectEmbeddedImagesFromMarkdown(cell, urls);
          }
        }
      } else if (Array.isArray(v)) {
        collectEmbeddedImagesFromMarkdown(v as MdPiece[], urls);
      }
    }
  }
};

type FileUploadPreviewProps = {
  item?: MessageItem;
  files: (File | AttachedFile)[];
  onDelete?: (index: number) => void;
  fontSize?: number;
  readOnly?: boolean;
  markdown?: boolean;
};

export function FileUploadPreview({
  item,
  files,
  onDelete,
  fontSize,
  readOnly,
  markdown,
}: FileUploadPreviewProps) {
  const [previews, setPreviews] = useState<IndexedFile[]>();
  const [links, setLinks] = useState<IndexedFile[]>();

  useEffect(() => {
    const go = async (
      files: (File | AttachedFile)[],
      markdown?: boolean,
      item?: MessageItem
    ) => {
      if (files) {
        if (markdown && files.length > 1) {
          throw new Error("unsupported");
        }

        // 0. try enriching file attributes from st_files before categorizing them
        // because we can't categorize a file unless we know its type
        if (item?.st_files) {
          for (const f of files) {
            // in markdown there is only name and uri, we don't really know content type and image size
            // we'll try carrying over those attribute from st_files
            if (!(f instanceof File)) {
              const orig = item.st_files.find((e) => e.uri == f.uri);
              if (orig) {
                if (orig.name && !f.name) {
                  f.name = orig.name;
                }
                if (orig.size && !f.size) {
                  f.size = orig.size;
                }
                if (orig.type && !f.type) {
                  f.type = orig.type;
                }
              }
            }
          }
        }

        const cat = categorizeFilesForDisplay(files, undefined, true);
        // filter out files that are already displayed as part of Markdown

        // 1. collect markdown files
        const markdownFiles: string[] = [];
        if (
          item?.st_pp &&
          (item?.st_source === "github" || item?.st_source === "gitlab") &&
          !markdown
        ) {
          let pp: MdPiece[] = [];
          if (typeof item.st_pp === "string") {
            pp = await parseMarkdown(item.st_pp, item);
          } else {
            pp = item.st_pp;
          }
          collectEmbeddedImagesFromMarkdown(pp, markdownFiles);
        }

        // 2. filter out markdown files
        for (const uri of markdownFiles) {
          cat.previews = cat.previews.filter((e) => e.fileOrUrl !== uri);
          cat.links = cat.links.filter((e) => e.fileOrUrl !== uri);
        }

        // correct indecies to match those in st_files
        // again needed this for files embeded directly in markdown, like github's or gitlab's
        if (item?.st_files) {
          if (onDelete) {
            throw new Error(
              "unsupported! onDelete will require a different kind of index"
            );
          }

          const mapping = new Map<string, number>();
          item.st_files.forEach((f, i) => {
            if (f.uri) {
              mapping.set(f.uri, i);
            }
          });

          let fakeIndex = 67890;

          const correctIndecies = (linksOrPreviews: IndexedFile[]) => {
            linksOrPreviews.forEach((f, i) => {
              const url =
                typeof f.fileOrUrl === "string" ? f.fileOrUrl : undefined;
              const corrected = url ? mapping.get(url) : undefined;
              if (corrected !== undefined) {
                f.index = corrected;

                // in markdown there is only name and uri, we don't really know content type and image size
                // we'll try carrying over those attribute from st_files
                if (!(f instanceof File)) {
                  const orig = item.st_files![corrected];
                  if (orig.name) {
                    f.name = orig.name;
                  }
                  if (orig.size) {
                    f.size = orig.size;
                  }
                  if (orig.type) {
                    f.type = orig.type;
                  }
                }
              } else if (f.fileOrUrl instanceof File) {
                // this is a dummy preview, but it should use the correct index
                if (files.length !== item.st_files?.length) {
                  throw new Error("Should not get here");
                }
                f.index = i;
              } else {
                f.index = fakeIndex;
                fakeIndex += 1;
              }
            });
          };

          correctIndecies(cat.previews);
          correctIndecies(cat.links);
        }

        setPreviews(cat.previews.length ? cat.previews : undefined);
        setLinks(cat.links.length > 0 ? cat.links : undefined);
      } else {
        setPreviews(undefined);
        setLinks(undefined);
      }
    };
    go(files, markdown, item);
  }, [files, markdown, item]);

  const getStatus = () => {
    if (item && item.dummy) {
      return item.error ? "error" : "progress";
    }
    return undefined;
  };

  const handleError = (f: IndexedFile) => {
    setPreviews((prev) => {
      if (!prev) {
        return prev;
      }

      const found = prev.findIndex((e) => e.index === f.index);
      if (found >= 0) {
        if (prev.length === 1) {
          return undefined;
        }
        return prev.slice(0, found).concat(prev.slice(found + 1));
      }

      return prev;
    });

    setLinks((prev) => {
      if (!prev) {
        return [f];
      }

      const found = prev.findIndex((e) => e.index === f.index);
      if (found < 0) {
        return [...prev, f];
      }
      return prev;
    });
  };

  return (
    <>
      <Grid
        container
        direction={"row"}
        justifyItems={"flex-start"}
        alignItems={"flex-start"}
        gap={0.3}
        sx={{ width: "100%", maxWidth: "100%", overflow: "hidden" }}
      >
        {previews &&
          previews.map((f: IndexedFile, i) => (
            <Grid item key={`preview:${getFileKey(f)}`} sx={{ minWidth: 0 }}>
              <DeleteOverlay
                showStatus={getStatus()}
                onDelete={onDelete ? () => onDelete(f.index) : undefined}
              >
                <SingleFilePreview
                  fileObject={
                    f.fileOrUrl instanceof File ? f.fileOrUrl : undefined
                  }
                  fileAttachment={
                    !(f.fileOrUrl instanceof File)
                      ? { ...f, uri: f.fileOrUrl }
                      : undefined
                  }
                  fontSize={fontSize}
                  index={f.index}
                  readOnly={readOnly || item?.dummy}
                  item={item}
                  previewType={f.preview!}
                  onError={() => handleError(f)}
                />
              </DeleteOverlay>
            </Grid>
          ))}
      </Grid>

      {links && (
        <Stack>
          {links.map((f) => (
            <DeleteOverlay
              showStatus={getStatus()}
              onDelete={onDelete ? () => onDelete(f.index) : undefined}
              key={`link:${getFileKey(f)}`}
            >
              <DownloadLink file={f} item={item} />
            </DeleteOverlay>
          ))}
        </Stack>
      )}
    </>
  );
}

function DownloadLink({
  item,
  file,
  readOnly,
}: {
  item?: MessageItem;
  file: IndexedFile;
  readOnly?: boolean;
}) {
  const openNewTab = useDownloadAndOpenFile();

  return (
    <Stack direction="row" alignItems={"center"}>
      <Icon icon="uim:paperclip" style={{ paddingRight: 1 }} />
      <ButtonLink
        text={file.name || "attachment"}
        onclick={() => {
          if (file.fileOrUrl) {
            openNewTab(
              file.name || `attachment-${file.index}`,
              file.fileOrUrl,
              item
            );
          }
        }}
        disabled={readOnly || !file.fileOrUrl ? true : false}
      />
      <Box width={30}></Box>
    </Stack>
  );
}

type TextFilePreviewProps = {
  data?: File | string | Uint8Array;
  onError?: () => void;
  name?: string;
  mimetype?: string;
  fontSize?: number;
  index?: number;
  fileName?: string;
  readOnly?: boolean;
  item?: MessageItem;
  children?: (textData: string) => React.ReactNode;
};

function TextFilePreview({
  data,
  onError,
  fileName,
  mimetype,
  fontSize,
  index,
  readOnly,
  item,
  children,
}: TextFilePreviewProps) {
  const [contents, setContents] = useState<string | ArrayBuffer | Uint8Array>();
  useEffect(() => {
    if (data instanceof File) {
      const reader = new FileReader();
      reader.onload = () => {
        if (reader.result) {
          setContents(reader.result);
        }
      };
      reader.readAsArrayBuffer(data);
    } else if (typeof data === "string") {
      setContents(data);
    } else if (data instanceof Uint8Array) {
      setContents(data);
    }
  }, [data]);

  return (
    <>
      {contents && (
        <TextPreview
          data={contents}
          disableControls={readOnly}
          fontSize={fontSize}
          onError={() => onError!()}
          fileName={fileName || (data instanceof File ? data.name : undefined)}
          mimetype={mimetype || (data instanceof File ? data.type : undefined)}
          index={index}
          item={item}
          children={children}
        />
      )}
    </>
  );
}

export const isTextFileType = (f: { type?: string }): boolean => {
  if (isTextMimeType(f.type)) {
    return true;
  }
  return false;
};

type RemoteFile = {
  uri: string;
  name?: string;
  size?: number;
  type?: string;
};

function ImageWithToggle(props: ImageProps) {
  const toggle = useAtomValue(imageToggleAtom);
  if (!toggle) {
    return <CropOriginalIcon color="disabled" />;
  }

  return <Image {...props} />;
}

type SingleFilePreviewProps = {
  item?: MessageItem;
  fileObject?: File;
  fileAttachment?: RemoteFile;
  readOnly?: boolean;
  fontSize?: number;
  index?: number;
  previewType:
    | "pdf"
    | "text"
    | "image"
    | "json"
    | "diff"
    | "csv"
    | "audio"
    | "video";
  onError?: () => void;
};

function SingleFilePreview({
  item,
  fileObject,
  fileAttachment,
  readOnly,
  fontSize,
  index,
  previewType,
  onError,
}: SingleFilePreviewProps) {
  return (
    <>
      {true && (
        <>
          {previewType === "image" && (
            <ImageWithToggle
              image={
                fileAttachment
                  ? {
                      name: fileAttachment.name,
                      url: fileAttachment.uri,
                      mimetype: fileAttachment.type,
                    }
                  : undefined
              }
              fileObject={fileObject}
              item={item}
              index={index}
              fontSize={fontSize}
              readOnly={readOnly}
              onError={onError}
            />
          )}
          {previewType === "pdf" && (
            <EmbeddedFile
              fileDescriptor={
                fileAttachment
                  ? {
                      name: fileAttachment?.name,
                      url: fileAttachment?.uri!,
                      mimetype: fileAttachment?.type || "application/pdf",
                    }
                  : undefined
              }
              fileObject={fileObject}
              filetypes={new Set(["pdf"])}
              item={item}
              index={index}
              encoding="base64"
              readOnly={readOnly}
              onError={onError}
            >
              <Pdf fontSize={fontSize} />
            </EmbeddedFile>
          )}

          {previewType === "text" && (
            <EmbeddedFile
              fileDescriptor={
                fileAttachment
                  ? {
                      name: fileAttachment?.name,
                      url: fileAttachment?.uri!,
                      mimetype: fileAttachment?.type || "text/plain",
                    }
                  : undefined
              }
              fileObject={fileObject}
              item={item}
              index={index}
              readOnly={readOnly}
              onError={onError}
            >
              <TextFilePreview fontSize={fontSize} />
            </EmbeddedFile>
          )}
          {previewType === "json" && (
            <EmbeddedFile
              fileDescriptor={
                fileAttachment
                  ? {
                      name: fileAttachment?.name,
                      url: fileAttachment?.uri!,
                      mimetype: fileAttachment?.type || "application/json",
                    }
                  : undefined
              }
              fileObject={fileObject}
              item={item}
              index={index}
              readOnly={readOnly}
              onError={onError}
            >
              <TextFilePreview fontSize={fontSize}>
                {(textData: string) => <JSONPretty data={textData} />}
              </TextFilePreview>
            </EmbeddedFile>
          )}
          {previewType === "diff" && (
            <EmbeddedFile
              fileDescriptor={
                fileAttachment
                  ? {
                      name: fileAttachment?.name,
                      url: fileAttachment?.uri!,
                      mimetype: fileAttachment?.type || "text/x-diff",
                    }
                  : undefined
              }
              fileObject={fileObject}
              item={item}
              index={index}
              readOnly={readOnly}
              onError={onError}
            >
              <>
                {item?.st_type === "diff" && (
                  <CollapsibleTextPreview
                    fontSize={fontSize}
                    text={getCollapsibleDiffTitle(item)}
                  >
                    {(textData: string) => <UnidiffRust data={textData} />}
                  </CollapsibleTextPreview>
                )}
                {item?.st_type !== "diff" && (
                  <TextFilePreview fontSize={fontSize}>
                    {(textData: string) => <UnidiffRust data={textData} />}
                  </TextFilePreview>
                )}
              </>
            </EmbeddedFile>
          )}
          {previewType === "audio" && (
            <EmbeddedFile
              fileDescriptor={
                fileAttachment
                  ? {
                      name: fileAttachment?.name,
                      url: fileAttachment?.uri!,
                      mimetype: fileAttachment?.type || "audio/unknown",
                    }
                  : undefined
              }
              fileObject={fileObject}
              item={item}
              index={index}
              readOnly={readOnly}
              onError={onError}
            >
              <DownloadLink
                item={item}
                file={{
                  index: index || 0,
                  fileOrUrl: fileObject || fileAttachment!.uri,
                  preview: "audio",
                  name: fileObject?.name || fileAttachment?.name,
                  size: fileObject?.size || fileAttachment?.size,
                  type: fileObject?.type || fileAttachment?.type,
                }}
              />
              <AudioPlayback fontSize={fontSize} />
            </EmbeddedFile>
          )}
          {previewType === "video" && (
            <EmbeddedFile
              fileDescriptor={
                fileAttachment
                  ? {
                      name: fileAttachment?.name,
                      url: fileAttachment?.uri!,
                      mimetype: fileAttachment?.type || "video/unknown",
                    }
                  : undefined
              }
              fileObject={fileObject}
              item={item}
              index={index}
              readOnly={readOnly}
              onError={onError}
            >
              <DownloadLink
                item={item}
                file={{
                  index: index || 0,
                  fileOrUrl: fileObject || fileAttachment!.uri,
                  preview: "video",
                  name: fileObject?.name || fileAttachment?.name,
                  size: fileObject?.size || fileAttachment?.size,
                  type: fileObject?.type || fileAttachment?.type,
                }}
              />
              <VideoPlayback fontSize={fontSize} />
            </EmbeddedFile>
          )}
          {previewType === "csv" && (
            <EmbeddedFile
              fileDescriptor={
                fileAttachment
                  ? {
                      name: fileAttachment?.name,
                      url: fileAttachment?.uri!,
                      mimetype: fileAttachment?.type || "text/csv",
                    }
                  : undefined
              }
              fileObject={fileObject}
              item={item}
              index={index}
              readOnly={readOnly}
              onError={onError}
            >
              <TextFilePreview fontSize={fontSize}>
                {(textData: string) => {
                  // Validate CSV format
                  try {
                    if (!riki.validate_csv_format_str(textData)) {
                      return textData;
                    }
                    // If validation succeeds, show CSV table
                    return (
                      <CsvPreview
                        data={textData}
                        fontSize={fontSize}
                        maxRows={50}
                      />
                    );
                  } catch {
                    // If validation fails, return null → TextFilePreview shows raw text
                    return textData;
                  }
                }}
              </TextFilePreview>
            </EmbeddedFile>
          )}
        </>
      )}
    </>
  );
}

const getDiffColors = (isDark: boolean) => ({
  add: isDark ? "#4ade80" : "#2dfc50",
  del: isDark ? "#f87171" : "#ff3030",
});

type DiffLine = {
  line: string;
  color?: string;
};

const parseUnidiff = (
  data: string,
  colorAdd: string,
  colorDel: string
): DiffLine[] => {
  let out: DiffLine[] = data.split("\n").map((e) => ({
    line: e,
  }));

  for (let i = 3; i < out.length; ++i) {
    const cur = out[i];
    if (cur.line.length > 0) {
      if (cur.line[0] === "+") {
        cur.color = colorAdd;
      } else if (cur.line[0] === "-") {
        cur.color = colorDel;
      } else if (cur.line[0] === "@") {
        out[i - 1].color = undefined;
        out[i - 2].color = undefined;
      }
    }
  }

  return out;
};

type UnidiffProps = {
  data: string;
};

export function UnidiffJs({ data }: UnidiffProps) {
  const theme = useTheme();
  const colors = getDiffColors(theme.palette.mode === "dark");
  const [lines, setLines] = useState<DiffLine[]>(
    parseUnidiff(data, colors.add, colors.del)
  );
  const ref = useRef(data);

  useEffect(() => {
    if (ref.current !== data) {
      ref.current = data;
      setLines(parseUnidiff(data, colors.add, colors.del));
    }
  }, [data, colors.add, colors.del]);

  return (
    <>
      {lines.map((line: DiffLine, i: number) => (
        <div key={i} style={{ color: line.color }}>
          {line.line}
        </div>
      ))}
    </>
  );
}

export function UnidiffRust({ data }: UnidiffProps) {
  const theme = useTheme();
  const colors = getDiffColors(theme.palette.mode === "dark");

  return (
    <div
      dangerouslySetInnerHTML={{
        __html: riki.colorize_diff(data, colors.add, colors.del),
      }}
    />
  );
}

export function CollapsibleDiff({ data }: UnidiffProps) {
  const [collapsed, setCollapsed] = useState(true);

  return (
    <>
      {!collapsed && <UnidiffRust data={data} />}

      {collapsed && (
        <ButtonLink
          text="Click here to show a full diff."
          onclick={() => setCollapsed(false)}
        />
      )}
    </>
  );
}

type DeleteOverlayProps = {
  onDelete?: () => void;
  children: React.ReactNode;
  showStatus?: string;
};

function DeleteOverlay({ onDelete, showStatus, children }: DeleteOverlayProps) {
  const theme = useTheme();
  const [hover, setHover] = useState(false);

  const handleDelete = () => {
    if (onDelete) {
      onDelete();
    }
  };

  return (
    <Box
      component="span"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <Box sx={{ position: "relative", display: "block", maxWidth: "100%" }}>
        {onDelete && hover && (
          <Box sx={{ position: "absolute", right: 0, zIndex: 5 }}>
            <IconButton onClick={handleDelete} size="small">
              <CancelIcon
                fontSize="inherit"
                sx={{
                  borderRadius: "50%",
                  background:
                    theme.palette.mode === "dark" ? "#2a2a2a" : "#ffffff",
                }}
              />
            </IconButton>
          </Box>
        )}
        {showStatus === "error" && (
          <Box
            sx={{
              position: "absolute",
              left: 0,
              height: "100%",
              width: "100%",
              zIndex: 5,
            }}
            display="flex"
            justifyContent="center"
            alignItems="center"
          >
            <ErrorIcon
              sx={{
                color: theme.palette.mode === "dark" ? "#f87171" : "#dc2626",
              }}
            />
          </Box>
        )}
        {showStatus === "progress" && (
          <Box
            sx={{
              position: "absolute",
              left: 0,
              height: "100%",
              width: "100%",
              zIndex: 5,
            }}
            display="flex"
            justifyContent="center"
            alignItems="flex-start"
          >
            <Box sx={{ width: "97%", m: 0.1 }}>
              <LinearProgress
                color="secondary"
                sx={{
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.1)"
                      : "#d3d3d3",
                  "& .MuiLinearProgress-bar": {
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? "rgba(255, 255, 255, 0.3)"
                        : "#808080",
                  },
                }}
              />
            </Box>
          </Box>
        )}
        <>{children}</>
      </Box>
    </Box>
  );
}

type AudioPlaybackProps = {
  data?: string | File | Uint8Array;
  item?: MessageItem;
  onError?: (error?: Error) => void;
  fontSize?: number;
  readOnly?: boolean;
  index?: number;
  fileName?: string;
  mimetype?: string;
};

export function AudioPlayback({
  data,
  item,
  onError,
  fontSize,
  readOnly,
  index,
  mimetype,
  fileName,
}: AudioPlaybackProps) {
  const [audioData, setAudioData] = useState<File>();

  useEffect(() => {
    if (typeof data === "string") {
      toBytes(data).then((arr: Uint8Array) => {
        const f = new File([arr.buffer], fileName || "audio", {
          type: mimetype || "audio/unknown",
        });
        setAudioData(f);
      });
    } else if (data instanceof File) {
      return setAudioData(data);
    } else if (data instanceof Uint8Array) {
      const f = new File([data.buffer], fileName || "audio", {
        type: mimetype || "audio/unknown",
      });
      setAudioData(f);
    }
  }, [data]);

  return (
    <>
      {audioData && (
        <Box width={400} fontSize={10}>
          <audio src={URL.createObjectURL(audioData)} controls />
        </Box>
      )}
    </>
  );
}

type VideoPlaybackProps = {
  data?: string | File | Uint8Array;
  item?: MessageItem;
  onError?: (error?: Error) => void;
  fontSize?: number;
  readOnly?: boolean;
  index?: number;
  fileName?: string;
  mimetype?: string;
};

export function VideoPlayback({
  data,
  item,
  onError,
  fontSize,
  readOnly,
  index,
  mimetype,
  fileName,
}: VideoPlaybackProps) {
  const [videoData, setvideoData] = useState<File>();

  useEffect(() => {
    if (typeof data === "string") {
      toBytes(data).then((arr: Uint8Array) => {
        const f = new File([arr.buffer], fileName || "video", {
          type: mimetype || "video/unknown",
        });
        setvideoData(f);
      });
    } else if (data instanceof File) {
      return setvideoData(data);
    } else if (data instanceof Uint8Array) {
      const f = new File([data.buffer], fileName || "video", {
        type: mimetype || "video/unknown",
      });
      setvideoData(f);
    }
  }, [data]);

  return (
    <>
      {videoData && (
        <video
          controls
          style={{
            maxWidth: "100%",
            width: "auto",
            maxHeight: "70vh",
            display: "block",
          }}
        >
          <source src={URL.createObjectURL(videoData)} type="video/mp4" />
        </video>
      )}
    </>
  );
}

function getCollapsibleDiffTitle(item: MessageItem) {
  if (item.st_url && item.st_url.includes("commit")) {
    const path = item.st_url.split("/");
    const commitHash = path[path.length - 1];
    const shortHash = commitHash.slice(0, 7);
    return `Click here to show full diff -- ${shortHash}.`;
  }
  return "Click here to show full diff.";
}

type CollapsibleTextPreviewProps = TextFilePreviewProps & { text?: string };

function CollapsibleTextPreview({
  data,
  onError,
  fileName,
  mimetype,
  fontSize,
  index,
  readOnly,
  item,
  children,
  text,
}: CollapsibleTextPreviewProps) {
  const theme = useTheme();
  const [collapsed, setCollapsed] = useState(true);
  return (
    <>
      {collapsed && (
        <Box
          sx={{
            backgroundColor:
              theme.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.08)"
                : "#eeeeee",
            borderRadius: 1.5,
            pl: 2,
            pr: 2,
          }}
        >
          <ButtonLink
            text={text || "Click here to show file contents"}
            onclick={() => setCollapsed(false)}
            fontSize={9}
          />
        </Box>
      )}
      {!collapsed && (
        <TextFilePreview
          data={data}
          onError={onError}
          fileName={fileName}
          mimetype={mimetype}
          fontSize={fontSize}
          index={index}
          readOnly={readOnly}
          item={item}
        >
          {children}
        </TextFilePreview>
      )}
    </>
  );
}
